import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VacancyPatternStatusesEnum } from '@thinknet-gmbh/platform-common';
import {
  Button,
  ButtonsWrapper,
  CheckboxSelect,
  Form,
  Icon,
  ICONS,
  Loader,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextAreaInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';

import { IVacancyFormProps } from 'src/pages/vacancies/vacancies.interfaces';
import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';
import { useGetJobLocationsByDepartment } from 'src/hooks/jobs/get-job-locations-by-department.hook';
import { useCreateVacancyPattern } from 'src/hooks/vacancies-patterns/create-vacancy-pattern.hook';
import { useGetCurrentCompany } from 'src/hooks/companies/get-current-company.hook';
import { useUpdateVacancyById } from 'src/hooks/vacancies-patterns/update-vacancy-by-id.hook';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';

import {
  FormContentWrapper,
  DeleteContainer,
  FormListContentWrapper,
} from './vacancies-form.styles';
import { resetFields } from './vacancies-form.helpers';
import UploadFormItem from './upload-form-item.component';

const VacanciesForm = ({
  onClose,
  initialValues,
  isModal,
  onSuccess,
  vacancyId,
}: IVacancyFormProps) => {
  const { t } = useTranslation(['jobs', 'common']);
  const [form] = Form.useForm();
  const [departmentId, setDepartmentId] = useState('');
  const { companies } = useGetAllPlatformCompanies();
  const { company } = useGetCurrentCompany();
  const { createVacancyPattern, loading: createLoading } = useCreateVacancyPattern({ onSuccess });
  const { updateVacancy, loading: updateLoading } = useUpdateVacancyById({ onSuccess });
  const { jobs } = useGetJobLocationsByDepartment(
    departmentId || (initialValues && initialValues.departmentId)
  );
  const { employees } = useGetAllEmployeesForList({
    filter: { companyId: company._id, accountActive: true },
  });

  const setStatus = (status: VacancyPatternStatusesEnum) => {
    if (!initialValues || isModal) {
      form.setFieldsValue({ status });
    }
  };

  const handleSelect = (field: string) => {
    resetFields(field, form);
  };

  const onFinish = (values: any) => {
    const skills = values.skills.includes('') ? undefined : values.skills;
    const { uploadedFile, ...rest } = values;

    if (vacancyId) {
      return updateVacancy({
        variables: {
          payload: {
            ...rest,
            headerImageUrl: uploadedFile && uploadedFile.file,
          },
          vacancyId,
        },
      });
    }

    createVacancyPattern({
      variables: {
        payload: {
          ...rest,
          skills,
          workspaceCompanyId: company._id,
          headerImageUrl: uploadedFile && uploadedFile.file,
        },
      },
    });
  };

  if (createLoading || updateLoading) {
    return <Loader />;
  }

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <FormContentWrapper isModal={isModal}>
        <div>
          <Form.Item
            name="companyId"
            label={t('common:labels.company')}
            rules={[{ required: true, message: t('jobs:validation.companyRequired') }]}
          >
            <SelectInput placeholder="Asteric.Co" onSelect={() => handleSelect('departmentId')}>
              {companies.map(({ _id, name }: any) => (
                <SelectInput.Option key={_id} value={_id}>
                  {name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const companyId = getFieldValue('companyId');

              const company = companies.find((item: any) => item._id === companyId);

              return (
                <Form.Item
                  name="departmentId"
                  label={t('common:labels.department')}
                  rules={[
                    { required: true, message: t('common:validation.error.departmentRequired') },
                  ]}
                >
                  <SelectInput
                    disabled={!companyId}
                    placeholder="Mauris netus"
                    onSelect={id => {
                      setDepartmentId(id);
                      handleSelect('jobId');
                    }}
                  >
                    {(company ? company.departments : []).map(({ _id, name }: any) => (
                      <SelectInput.Option key={_id} value={_id}>
                        {name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name="jobId"
                label={t('common:labels.jobTitle')}
                rules={[{ required: true, message: t('common:validation.error.jobTitleRequired') }]}
              >
                <SelectInput
                  placeholder={t('settings:permissionGroupSettings.placeholders.builders')}
                  disabled={!getFieldValue('departmentId')}
                  onSelect={() => handleSelect('jobLocationId')}
                >
                  {jobs.map(({ job }: any) => (
                    <SelectInput.Option key={job._id} value={job._id}>
                      {job.title}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const jobId = getFieldValue('jobId');

              const jobLocations = jobs.find((item: any) => item.job._id === jobId);

              return (
                <Form.Item
                  name="jobLocationId"
                  label={t('common:labels.location')}
                  rules={[
                    { required: true, message: t('common:validation.error.locationRequired') },
                  ]}
                >
                  <SelectInput placeholder="Hamburg" disabled={!jobId}>
                    {(jobLocations ? jobLocations.job.jobLocations : []).map(
                      ({ companyLocation, _id }: any) => (
                        <SelectInput.Option key={_id} value={_id}>
                          {companyLocation.name}
                        </SelectInput.Option>
                      )
                    )}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="status"
            label={t('common:labels.status')}
            rules={[
              {
                required: !!initialValues && !isModal,
                message: t('processBuilder:validation.statusRequired'),
              },
            ]}
            hidden={!initialValues || isModal}
          >
            <SelectInput>
              {Object.keys(VacancyPatternStatusesEnum).map(status => (
                <SelectInput.Option key={status} value={status}>
                  {t(`vacancies:labels.statuses.${status.toLowerCase()}`)}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>

          <Form.Item
            name="responsiblePersonsIds"
            label={t('vacancies:labels.responsiblePersons')}
            rules={[
              { required: true, message: t('vacancies:validation.responsiblePersonsRequired') },
            ]}
          >
            <CheckboxSelect
              placeholder="Select from the list"
              optionData={employees.map((item: any) => ({
                label: item.profile.displayName,
                value: item._id,
              }))}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) =>
              (!isModal || getFieldValue('jobLocationId')) && (
                <>
                  <Form.Item name="jobDescription" label={t('jobs:labels.jobDescription')}>
                    <TextAreaInput
                      showCount
                      maxLength={2000}
                      placeholder={t('products:planList.createPlan.placeholders.writeDescription')}
                    />
                  </Form.Item>
                  <UploadFormItem t={t} headerImage={initialValues && initialValues.headerImage} />
                </>
              )
            }
          </Form.Item>
          {!isModal && (
            <ButtonsWrapper marginTop={40}>
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setStatus(VacancyPatternStatusesEnum.PUBLISHED)}
              >
                {initialValues ? t('common:buttons.save') : t('jobs:buttons.publish')}
              </Button>
              {!initialValues && (
                <Button
                  htmlType="submit"
                  onClick={() => setStatus(VacancyPatternStatusesEnum.DRAFT)}
                >
                  {t('jobs:buttons.saveAsDraft')}
                </Button>
              )}
              <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
            </ButtonsWrapper>
          )}
        </div>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) =>
            (!isModal || getFieldValue('jobLocationId')) && (
              <Form.List name="skills" initialValue={initialValues && !isModal ? undefined : ['']}>
                {(fields, { add, remove }) => (
                  <FormListContentWrapper
                    isIcon={fields.length > 1}
                    isCreate={!initialValues}
                    isModal={isModal}
                  >
                    <div>
                      {fields.map(field => (
                        <div key={field.key}>
                          <DeleteContainer isIcon={fields.length > 1}>
                            <Form.Item
                              name={[field.name, 'name']}
                              label={t('jobs:labels.neededSkills')}
                            >
                              <TextInput
                                maxLength={100}
                                placeholder={t('jobs:labels.teamPlayer')}
                              />
                            </Form.Item>
                            {fields.length > 1 && (
                              <Icon
                                icon={ICONS.trash}
                                rightMargin={false}
                                color={PLATFORM_THEME_COLORS.tone.red}
                                size={22}
                                onClick={() => remove(field.name)}
                              />
                            )}
                          </DeleteContainer>
                          {field.name === fields.length - 1 && (
                            <Button
                              type="text"
                              icon={ICONS.plus}
                              iconColor={PLATFORM_THEME_COLORS.primary.blue}
                              onClick={() => add()}
                            >
                              {t('jobs:buttons.addSkill')}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </FormListContentWrapper>
                )}
              </Form.List>
            )
          }
        </Form.Item>

        {isModal && (
          <ButtonsWrapper marginTop={16}>
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setStatus(VacancyPatternStatusesEnum.PUBLISHED)}
            >
              {t('jobs:buttons.publish')}
            </Button>

            <Button htmlType="submit" onClick={() => setStatus(VacancyPatternStatusesEnum.DRAFT)}>
              {t('jobs:buttons.saveAsDraft')}
            </Button>
          </ButtonsWrapper>
        )}
      </FormContentWrapper>
    </Form>
  );
};

export default VacanciesForm;
